import { RUN_KEYS, SECTORS, ZONE_ORDER, runKey, RUN_COLOR, shotLabel, unit } from "./shotData";
import type { LiveBall } from "./useLiveStats";

type Mode = "spike" | "zone";

/**
 * Wagon wheel for one batter. The scorer records a field sector per shot, not an angle or a distance, so a spike's angle is
 * placed within its sector and its length follows the runs (boundaries reach the rope). Zone mode is exact.
 */
export const ShotField = ({ balls, left, mode, size = 230, label }: { balls: LiveBall[]; left: boolean; mode: Mode; size?: number; label: string }) => {
  const sz = size, c = sz / 2, R = sz / 2 - 3, oy = c - sz * 0.075, sx = left ? 1 : -1;
  const P = (a: number, r: number): [number, number] => [c + sx * Math.sin((a * Math.PI) / 180) * r, c + Math.cos((a * Math.PI) / 180) * r];
  const txt = (x: number, t: string, anchor: "start" | "end") => (
    <text x={x} y={sz * 0.07} fontSize={sz * 0.042} fontWeight={800} fill="#33602A" textAnchor={anchor} letterSpacing=".5">{t}</text>
  );
  const total = balls.reduce((a, b) => a + b.runs, 0);

  let body;
  if (mode === "zone") {
    const zones = ZONE_ORDER.map((k) => ({ k, ...SECTORS[k], r: balls.filter((b) => b.sector === k).reduce((a, b) => a + b.runs, 0) }));
    const mx = Math.max(1, ...zones.map((z) => z.r));
    const sum = total || 1;
    body = zones.map((z) => {
      const [x0, y0] = P(z.from, R), [x1, y1] = P(z.to, R), k = z.r / mx;
      const [tx, ty] = P((z.from + z.to) / 2, R * 0.66);
      const dark = k > 0.55 && z.r > 0;
      return (
        <g key={z.k}>
          <path d={`M${c} ${c}L${x0.toFixed(1)} ${y0.toFixed(1)}A${R} ${R} 0 0 ${left ? 0 : 1} ${x1.toFixed(1)} ${y1.toFixed(1)}Z`} fill={z.r ? `rgba(31,94,51,${(0.12 + 0.7 * k).toFixed(2)})` : "rgba(255,255,255,0)"} stroke="#fff" strokeOpacity={0.7} strokeWidth={1.2}>
            <title>{`${z.label}: ${z.r} run${z.r === 1 ? "" : "s"} (${Math.round((z.r / sum) * 100)}%)`}</title>
          </path>
          <text x={tx} y={ty + sz * 0.012} textAnchor="middle" fontFamily="var(--num)" fontSize={sz * 0.072} fontWeight={700} fill={dark ? "#fff" : "#1E3D27"}>{z.r}</text>
          <text x={tx} y={ty + sz * 0.058} textAnchor="middle" fontSize={sz * 0.036} fontWeight={700} fill={dark ? "#E3F2DA" : "#3F6B4A"}>{z.label}</text>
        </g>
      );
    });
  } else {
    body = (
      <>
        <rect x={c - sz * 0.028} y={c - sz * 0.095} width={sz * 0.056} height={sz * 0.19} rx={2} fill="#E8DCAA" />
        {RUN_KEYS.flatMap((k) =>
          balls.filter((b) => runKey(b.runs) === k).map((b) => {
            const sec = SECTORS[b.sector as string];
            if (!sec) return null;
            const ang = sec.from + 6 + unit(b.ballId, 1) * (sec.to - sec.from - 12);
            const len = R * (k === "6" ? 0.985 : k === "4" ? 0.97 : k === "3" ? 0.7 + unit(b.ballId, 2) * 0.12 : k === "2" ? 0.5 + unit(b.ballId, 2) * 0.14 : 0.28 + unit(b.ballId, 2) * 0.16);
            const rad = (ang * Math.PI) / 180;
            // spikes start at the batter, a little above the centre of the circle
            const ex = c + sx * Math.sin(rad) * len, ey = oy + Math.cos(rad) * len;
            return (
              <g key={b.ballId}>
                <line x1={c} y1={oy} x2={ex.toFixed(1)} y2={ey.toFixed(1)} stroke={RUN_COLOR[k]} strokeWidth={k === "1" ? 1.4 : 1.9} strokeLinecap="round">
                  <title>{`${b.runs} · ${shotLabel(b.shotType) ?? sec.label} · ${b.over}.${b.ball} off ${b.bowler}`}</title>
                </line>
                {k === "6" ? <circle cx={ex.toFixed(1)} cy={ey.toFixed(1)} r={2.6} fill={RUN_COLOR["6"]} stroke="#fff" strokeWidth={1} /> : null}
              </g>
            );
          }),
        )}
        <circle cx={c} cy={oy} r={2.6} fill="#fff" stroke="#2F5A41" strokeWidth={1} />
      </>
    );
  }

  return (
    <svg viewBox={`0 0 ${sz} ${sz}`} role="img" aria-label={`${mode === "zone" ? "Wagon zones" : "Spike graph"} for ${label}: ${total} runs shown`}>
      <circle cx={c} cy={c} r={R} fill="#A9D48F" />
      <circle cx={c} cy={c} r={R * 0.56} fill="#98C97C" />
      {body}
      {txt(sz * 0.1, left ? "LEG" : "OFF", "start")}
      {txt(sz * 0.9, left ? "OFF" : "LEG", "end")}
    </svg>
  );
};
